import { zodResolver } from '@hookform/resolvers/zod';
import { LOGIN_HINT, loginSchema, shouldShowLoginHint } from '@fitness/core';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Lightbulb } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { SocialButtons } from '@/components/SocialButtons';
import { useAuth } from '@/lib/auth';
import { applyFieldErrors, countLoginFailure, explainError } from '@/lib/errors';
import { safeRedirect } from '@/lib/redirect';

interface LoginSearch {
  /** Ruta a la que volver tras iniciar sesión. */
  redirect?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const target = search['redirect'];
    return typeof target === 'string' ? { redirect: target } : {};
  },
  beforeLoad: ({ context, search }) => {
    if (context.auth.status === 'authenticated') {
      throw redirect({ href: safeRedirect(search.redirect) });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [formError, setFormError] = useState<{ message: string; canRetry: boolean } | null>(null);
  // Fallos de contraseña seguidos: a partir de tres se da una pista concreta
  // en vez de repetir el mismo aviso (hubo quien lo intentó 17 veces a ciegas).
  const [failures, setFailures] = useState(0);
  // Tras un 429, el botón espera lo que pide el servidor antes de dejar probar.
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [capsLock, setCapsLock] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  useEffect(() => {
    if (blockedUntil === null) return;
    const timer = window.setTimeout(
      () => setBlockedUntil(null),
      Math.max(0, blockedUntil - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [blockedUntil]);

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await auth.login(values);
      await navigate({ href: safeRedirect(search.redirect), replace: true });
    } catch (error) {
      const explained = explainError(error);
      setFailures((current) => countLoginFailure(current, error));
      if (explained.retryAfterSeconds !== null) {
        setBlockedUntil(Date.now() + explained.retryAfterSeconds * 1000);
      }
      // Un dato mal escrito se dice en su campo, no en un aviso genérico.
      if (applyFieldErrors(explained, ['email', 'password'] as const, setError)) return;
      setFormError({ message: explained.message, canRetry: explained.canRetry });
    }
  });

  function trackCapsLock(event: KeyboardEvent<HTMLInputElement>): void {
    setCapsLock(event.getModifierState('CapsLock'));
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-app flex-col justify-center gap-6 p-6">
      <header className="text-center">
        <img src="/icons/icon-192.png" alt="" className="mx-auto size-16 rounded-2xl" />
        <h1 className="mt-4 text-2xl font-bold">Entra en Fitness</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Tu entrenamiento, tu nutrición y tu progreso en un mismo sitio.
        </p>
      </header>

      <SocialButtons redirect={safeRedirect(search.redirect)} />

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? (
          <ErrorBanner
            title="No se ha podido entrar"
            message={formError.message}
            {...(formError.canRetry ? { onRetry: () => void onSubmit() } : {})}
          />
        ) : null}

        {shouldShowLoginHint(failures) ? (
          <div
            role="status"
            className="rounded-xl border border-line bg-surface-raised p-3 text-sm text-ink"
          >
            <p className="flex items-center gap-2 font-semibold">
              <Lightbulb className="size-4 shrink-0 text-brand" aria-hidden="true" />
              {LOGIN_HINT.title}
            </p>
            <ul className="mt-1.5 list-disc space-y-1 pl-5 text-ink-muted">
              {LOGIN_HINT.tips.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <Field htmlFor="email" label="Correo electrónico" error={errors.email?.message}>
          <Input
            {...fieldAria('email', errors.email?.message)}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="tu@correo.com"
            {...register('email')}
          />
        </Field>

        <Field
          htmlFor="password"
          label="Contraseña"
          error={errors.password?.message}
          hint={capsLock ? 'Tienes activadas las mayúsculas.' : undefined}
        >
          <Input
            {...fieldAria('password', errors.password?.message, capsLock)}
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            onKeyDown={trackCapsLock}
            onKeyUp={trackCapsLock}
            {...register('password')}
          />
        </Field>

        <Button
          type="submit"
          size="lg"
          block
          loading={isSubmitting}
          disabled={blockedUntil !== null}
        >
          {blockedUntil !== null ? 'Espera para volver a probar' : 'Iniciar sesión'}
        </Button>
      </form>

      <p className="text-center text-sm text-ink-muted">
        ¿Aún no tienes cuenta?{' '}
        <Link
          to="/register"
          className="font-semibold text-brand underline-offset-4 hover:underline"
        >
          Crea una gratis
        </Link>
      </p>
    </main>
  );
}
